// PRE RMA CSB — Import de consultations depuis l'ancien outil (fichier .html / .txt / .json OU texte collé).
// Fonctions PURES (aucun accès DOM, réseau ni IndexedDB) : testables seules ; l'écran SaisieUnique ne fait que lire le fichier
// (FileReader) et écrire le résultat dans IndexedDB. Chaque ligne est convertie vers la nomenclature OFFICIELLE (migration 005),
// validée avec le même moteur que la saisie (validerConsultation) ; les lignes invalides sont REFUSÉES avec leur raison, jamais corrigées en silence.
import { parseAge } from './age.ts';
import { DIAGNOSTICS_REF, RULES } from './constants.ts';
import { tranchePalu } from './tranches.ts';
import { validerConsultation } from './validation.ts';
import type { ConsultationInput } from './validation.ts';

/** Ligne brute, telle qu'extraite du fichier ou du texte (avant conversion). */
export interface LigneBrute {
  rce: string; fievre: string; tdr: string; age: string; genre: string;
  diagnostics: string[]; traumatisme: string; traitement: string; indicateur: string; admission: string;
}

export interface ResultatImport {
  valides: ConsultationInput[];
  rejets: Array<{ ligne: number; raison: string }>;
  /** Lignes ignorées car leur N° RCE existe déjà sur cet appareil pour la période (ou plus haut dans le fichier), dans le même registre. */
  doublons: number;
  /** Répartition des consultations « paludisme / fièvre » dans les 5 tranches (0-11 mois … 25 ans et plus). */
  tranchesPalu: Record<string, number>;
}

/** minuscules, sans accents ni ponctuation : « Écoulement Génital » → « ecoulement genital ». */
export function normaliser(s: unknown): string {
  return String(s ?? '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

// ---------------------------------------------------------------- Dictionnaire des diagnostics (ancienne liste → liste officielle)
/** Anciens libellés (liste de l'ancien outil « Pre RMA — Saisie CSB ») → code officiel (database/migrations/005_diagnostics_officiels.sql). */
const ANCIENS_DIAGNOSTICS: Record<string, string> = {
  'signe ibg': 'SIGNE_IBG', 'signes ibg': 'SIGNE_IBG', 'signes d ibg': 'SIGNE_IBG',
  'pneumonie severe': 'PNEUMONIE_SEVERE',
  'dysenterie': 'DYSENTERIE', 'diarrhee sanguinolente': 'DYSENTERIE',
  'diarrhee sans dh2o': 'DIARRHEE_SANS_DH2O', 'diarrhee sans deshydratation': 'DIARRHEE_SANS_DH2O',
  'diarrhee avec dh2o': 'DIARRHEE_AVEC_DH2O', 'diarrhee avec deshydratation': 'DIARRHEE_AVEC_DH2O',
  'sd grippal': 'SD_GRIPPAL', 'syndrome grippal': 'SD_GRIPPAL',
  'oreillon': 'OREILLON', 'oreillons': 'OREILLON',
  'infection respiratoire aigue': 'IRA', 'infection respiratoire aigu': 'IRA', 'ira': 'IRA',
  'pneumonie': 'PNEUMONIE',
  'toux ou rhume': 'TOUX_RHUME', 'toux rhume': 'TOUX_RHUME',
  'ecoulement genital': 'ECOULEMENT_GENITAL', 'ulceration genital': 'ULCERATION_GENITAL', 'ulceration genitale': 'ULCERATION_GENITAL',
  'paludisme simple': 'PALUDISME_SIMPLE',
  'paludisme chez fe': 'PALUDISME_FE', 'paludisme fe': 'PALUDISME_FE', 'paludisme chez femme enceinte': 'PALUDISME_FE',
  'paludisme grave': 'PALUDISME_GRAVE', 'paludisme grave autre que fe': 'PALUDISME_GRAVE',
  'toux chronique': 'TOUX_CHRONIQUE', 'suspicion de tuberculose': 'TOUX_CHRONIQUE',
  'lepre mb pb': 'LEPRE_MB_PB', 'lepre': 'LEPRE_MB_PB', // regroupée : on ne peut pas deviner PB ou MB (code d'ancienne saisie conservé)
  'anemie': 'ANEMIE', 'mam': 'MAM', 'mas': 'MAS', 'otite': 'OTITE',
  'oeil rouge traumatique': 'OEIL_ROUGE_TRAUMA', 'conjonctivite': 'CONJONCTIVITE',
  'carrie dentaire': 'CARRIE_DENTAIRE', 'caries dentaires': 'CARRIE_DENTAIRE', 'carie dentaire': 'CARRIE_DENTAIRE',
  'epilepsie': 'EPILEPSIE', 'hta': 'HTA',
  'susp diabete': 'SUSP_DIABETE', 'suspicion de diabete': 'SUSP_DIABETE',
  'lymphoedeme': 'LYMPHOEDEME', 'hydrocele': 'HYDROCELE',
  'douleur articulaire': 'DOULEUR_ARTICULAIRE', 'douleurs articulaires': 'DOULEUR_ARTICULAIRE',
  'ictere': 'ICTERE', 'autres': 'AUTRES',
};

let indexDiag: Map<string, string> | null = null;
function indexDiagnostics(): Map<string, string> {
  if (indexDiag) return indexDiag;
  const m = new Map<string, string>();
  for (const d of DIAGNOSTICS_REF) { m.set(normaliser(d.libelle), d.code); m.set(normaliser(d.code), d.code); }
  for (const [ancien, code] of Object.entries(ANCIENS_DIAGNOSTICS)) if (DIAGNOSTICS_REF.some((d) => d.code === code)) m.set(ancien, code);
  return (indexDiag = m);
}

/** Libellé (ancien OU officiel) ou code → code officiel ; null si inconnu. */
export function convertirDiagnostic(libelle: string): string | null {
  return indexDiagnostics().get(normaliser(libelle)) ?? null;
}

// ---------------------------------------------------------------- Extraction des lignes brutes
const VIDE: LigneBrute = { rce: '', fievre: '', tdr: '', age: '', genre: '', diagnostics: [], traumatisme: '', traitement: '', indicateur: '', admission: '' };
const txt = (v: unknown) => (v === null || v === undefined ? '' : String(v).trim());

/** Ordre des colonnes de l'ancien outil (bouton « Copier ») : N° RCE, Fièvre, TDR, Âge, Genre, Diagnostic 1-4, Traumatisme, Traitement palu, Indicateur CRÉNAS, Admission. */
function depuisCellules(c: string[], colonnes: Partial<Record<string, number>> | null): LigneBrute {
  const at = (i: number | undefined) => (i === undefined ? '' : txt(c[i]));
  if (!colonnes) {
    return { rce: at(0), fievre: at(1), tdr: at(2), age: at(3), genre: at(4), diagnostics: [5, 6, 7, 8].map(at).filter(Boolean), traumatisme: at(9), traitement: at(10), indicateur: at(11), admission: at(12) };
  }
  return {
    rce: at(colonnes.rce), fievre: at(colonnes.fievre), tdr: at(colonnes.tdr), age: at(colonnes.age), genre: at(colonnes.genre),
    diagnostics: [colonnes.d1, colonnes.d2, colonnes.d3, colonnes.d4].map(at).filter(Boolean),
    traumatisme: at(colonnes.traumatisme), traitement: at(colonnes.traitement), indicateur: at(colonnes.indicateur), admission: at(colonnes.admission),
  };
}

/** Si la ligne est un en-tête (contient « RCE » et « Genre »), retourne la correspondance colonne → index. */
function detecterEntete(cellules: string[]): Partial<Record<string, number>> | null {
  const n = cellules.map(normaliser);
  if (!n.some((x) => x.includes('rce')) || !n.some((x) => x === 'genre' || x === 'sexe')) return null;
  const col: Partial<Record<string, number>> = {};
  n.forEach((x, i) => {
    if (x.includes('rce') && col.rce === undefined) col.rce = i;
    else if (x.includes('fievre')) col.fievre = i;
    else if (x.startsWith('tdr')) col.tdr = i;
    else if (x.startsWith('age')) col.age = i;
    else if (x === 'genre' || x === 'sexe') col.genre = i;
    else if (/^diagnostic [1-4]$/.test(x)) col[`d${x.slice(-1)}`] = i;
    else if (x.includes('traumatisme')) col.traumatisme = i;
    else if (x.includes('traitement')) col.traitement = i;
    else if (x.includes('indicateur')) col.indicateur = i;
    else if (x.includes('admission')) col.admission = i;
  });
  return col;
}

function depuisObjet(o: Record<string, unknown>): LigneBrute {
  const diags = Array.isArray(o.diagnostics) ? o.diagnostics.map(txt) : [o.diag1, o.diag2, o.diag3, o.diag4].map(txt);
  return {
    ...VIDE,
    rce: txt(o.rce ?? o.numeroRceBrut), fievre: txt(o.fievre ?? o.casFievre), tdr: txt(o.tdr ?? o.tdrResultat), age: txt(o.ageTexte ?? o.ageBrut ?? o.age),
    genre: txt(o.genre), diagnostics: diags.filter(Boolean), traumatisme: txt(o.traumatisme ?? o.typeTraumatisme), traitement: txt(o.traitementPalu),
    indicateur: txt(o.indicateurCrenas), admission: txt(o.admissionCrenas),
  };
}

/** Cherche, dans un texte quelconque (script d'un fichier HTML, sauvegarde…), les tableaux JSON d'objets « consultation ». */
function extraireJson(s: string): LigneBrute[] {
  const out: LigneBrute[] = [];
  for (let i = s.indexOf('[{'); i >= 0; i = s.indexOf('[{', i + 1)) {
    let profondeur = 0, dansTexte = false, fin = -1;
    for (let j = i; j < s.length; j++) {
      const c = s[j];
      if (dansTexte) { if (c === '\\') j++; else if (c === '"') dansTexte = false; continue; }
      if (c === '"') dansTexte = true; else if (c === '[') profondeur++; else if (c === ']' && --profondeur === 0) { fin = j; break; }
    }
    if (fin < 0) continue;
    try {
      const v = JSON.parse(s.slice(i, fin + 1));
      if (Array.isArray(v) && v.length && v.every((x) => x && typeof x === 'object' && ('rce' in x || 'numeroRceBrut' in x))) { out.push(...v.map(depuisObjet)); i = fin; }
    } catch { /* ce n'est pas du JSON : on continue */ }
  }
  return out;
}

const ENTITES: Record<string, string> = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'", nbsp: ' ', '#39': "'" };
const decoder = (s: string) => s.replace(/&(#x?[0-9a-f]+|[a-z]+);/gi, (m, e: string) => {
  if (e[0] === '#') { const n = e[1].toLowerCase() === 'x' ? parseInt(e.slice(2), 16) : parseInt(e.slice(1), 10); return Number.isFinite(n) ? String.fromCodePoint(n) : m; }
  return ENTITES[e.toLowerCase()] ?? m;
});
const sansBalises = (s: string) => decoder(s.replace(/<br\s*\/?>/gi, ' ').replace(/<[^>]*>/g, '')).replace(/\s+/g, ' ').trim();

/** Lignes de tous les <table> d'un fichier HTML. */
function extraireTables(html: string): string[][] {
  const rows: string[][] = [];
  for (const tr of html.matchAll(/<tr[\s>][\s\S]*?<\/tr>/gi)) {
    const cells = [...tr[0].matchAll(/<t[dh][\s>][\s\S]*?<\/t[dh]>/gi)].map((c) => sansBalises(c[0]));
    if (cells.length) rows.push(cells);
  }
  return rows;
}

/** Contenu d'un fichier (.html, .txt, .json…) OU texte collé → lignes brutes. */
export function extraireLignes(contenu: string): LigneBrute[] {
  const json = extraireJson(contenu);
  if (json.length) return json;
  const estHtml = /<\s*(html|table|tr|body|div|pre)\b/i.test(contenu);
  let lignes: string[][];
  if (estHtml) {
    const tables = extraireTables(contenu);
    lignes = tables.length ? tables : decoder(contenu.replace(/<(script|style)[\s\S]*?<\/\1>/gi, '').replace(/<\/(p|div|li|pre|br)>|<br\s*\/?>/gi, '\n').replace(/<[^>]*>/g, '')).split(/\r?\n/).map((l) => l.split('\t'));
  } else {
    lignes = contenu.split(/\r?\n/).map((l) => (l.includes('\t') ? l.split('\t') : l.includes(';') ? l.split(';') : [l]));
  }
  let colonnes: Partial<Record<string, number>> | null = null;
  const out: LigneBrute[] = [];
  for (const cells of lignes) {
    if (cells.length < 5 || cells.every((c) => !c.trim())) continue; // ligne vide ou bruit (titre, texte libre)
    const entete = detecterEntete(cells);
    if (entete) { colonnes = entete; continue; }
    out.push(depuisCellules(cells, colonnes));
  }
  return out;
}

// ---------------------------------------------------------------- Conversion + validation
const choix = (v: string, valeurs: readonly string[]): string | null => {
  const n = normaliser(v);
  return valeurs.find((x) => normaliser(x) === n) ?? null;
};
/**
 * L'application impose l'unité selon l'âge (jours < 60 j, mois 2 à 59, ans ≥ 5) ; l'ancien outil était plus souple (« 3 ans », « 1 mois »).
 * Si l'âge est refusé tel quel, on le réécrit dans l'unité attendue (calcul exact : 3 ans = 36 mois, 1 mois = 30 jours).
 */
export function normaliserAge(brut: string): string {
  const direct = brut.trim();
  if (!direct || parseAge(direct).ok) return direct;
  const m = /^(\d+)\s*(jours?|j|mois|m|ans?|a)\.?$/i.exec(direct.replace(/[,.\s]+(?=[a-z])/i, ' '));
  if (!m) return direct;
  const n = Number(m[1]), u = m[2].toLowerCase();
  const jours = u.startsWith('j') ? n : u.startsWith('m') ? n * 30 : n * 365;
  const unite = u.startsWith('j') ? 'jours' : u.startsWith('m') ? 'mois' : 'ans';
  const candidat = unite === 'ans' ? (n < 5 ? `${n * 12} mois` : direct) : unite === 'mois' ? (n < 2 ? `${n * 30} jours` : n >= 60 ? `${Math.floor(n / 12)} ans` : direct) : (n >= 60 ? `${Math.floor(jours / 30)} mois` : direct);
  return parseAge(candidat).ok ? candidat : direct;
}

const TRAUMATISMES: Record<string, string> = { 'violence sexuelle': 'VIOLENCE_SEXUELLE', 'autre cas violence': 'AUTRE_VIOLENCE', 'autre cas traumatisme': 'AUTRE_TRAUMATISME' };
const TRAITEMENTS = ['ACT', 'Artesunate injectable', 'Autre', 'ACT, Artesunate injectable'] as const;

/** Convertit une ligne brute. Retourne la consultation ou la raison du refus. */
export function convertirLigne(l: LigneBrute): { ok: true; consultation: ConsultationInput } | { ok: false; raison: string } {
  const diagnostics: string[] = [];
  for (const d of l.diagnostics) {
    const code = convertirDiagnostic(d);
    if (!code) return { ok: false, raison: `Diagnostic inconnu : « ${d} »` };
    if (!diagnostics.includes(code)) diagnostics.push(code);
  }
  if (diagnostics.length > 4) return { ok: false, raison: 'Plus de 4 diagnostics' };

  const f = normaliser(l.fievre);
  const casFievre = ['oui', 'o', 'yes'].includes(f) ? 'Oui' : ['non', 'n', 'no'].includes(f) ? 'Non' : null;
  if (l.fievre && !casFievre) return { ok: false, raison: `Cas de fièvre invalide : « ${l.fievre} »` };

  const tdr = /\b(100|101|110|111)\b/.exec(l.tdr)?.[1] ?? '';
  if (l.tdr && !tdr) return { ok: false, raison: `Résultat TDR invalide : « ${l.tdr} »` };

  const g = normaliser(l.genre);
  const genre = ['m', 'h', 'masculin', 'homme', 'garcon'].includes(g) ? 'M' : ['f', 'feminin', 'femme', 'fille'].includes(g) ? 'F' : null;
  if (l.genre && !genre) return { ok: false, raison: `Genre invalide : « ${l.genre} »` };

  const tr = normaliser(l.traumatisme);
  const typeTraumatisme = !tr || tr === 'aucun' ? null : TRAUMATISMES[tr] ?? null;
  if (tr && tr !== 'aucun' && !typeTraumatisme) return { ok: false, raison: `Type de traumatisme inconnu : « ${l.traumatisme} »` };

  const traitementPalu = l.traitement ? choix(l.traitement.replace(/\s*,\s*/g, ', '), TRAITEMENTS) : null;
  if (l.traitement && !traitementPalu) return { ok: false, raison: `Traitement palu inconnu : « ${l.traitement} »` };

  const admissionCrenas = l.admission ? choix(l.admission, RULES.crenas.admissions) : null;
  if (l.admission && !admissionCrenas) return { ok: false, raison: `Admission CRENAS inconnue : « ${l.admission} »` };

  return {
    ok: true,
    consultation: {
      numeroRceBrut: l.rce.trim(), casFievre: casFievre as ConsultationInput['casFievre'], tdrResultat: (tdr || null) as ConsultationInput['tdrResultat'],
      ageBrut: normaliserAge(l.age) || null, genre: genre as ConsultationInput['genre'], diagnostics, typeTraumatisme, traitementPalu,
      indicateurCrenas: l.indicateur.trim() || null, admissionCrenas,
    },
  };
}

// ---------------------------------------------------------------- Registres + doublons
/** Registre déduit de l'âge : une unité « ans » (≥ 5 ans, après normalisation) → 5 ans et plus ; sinon moins de 5 ans. */
export function registreDeAge(age: string | null | undefined): 'MOINS5' | 'PLUS5' {
  return /\d\s*ans?\b/i.test(normaliserAge(age ?? '')) ? 'PLUS5' : 'MOINS5';
}
/** Clé de détection des doublons : un même N° peut exister dans chaque registre. */
export const cleDoublon = (numero: string, age: string | null | undefined) => `${registreDeAge(age)}|${numero.trim()}`;

/** Consultation déjà présente : objet (avec âge, recommandé) ou simple N° RCE (ancien format, bloque les deux registres). */
export type Existant = string | { numeroRceBrut: string; ageBrut?: string | null };

/**
 * Analyse complète (sans rien écrire) : extraction, conversion, validation, détection des doublons.
 * Les doublons sont détectés PAR REGISTRE (moins de 5 ans / 5 ans et plus) : le N° 12 peut exister dans les deux.
 * « CRENAS » n'est jamais un doublon.
 */
export function analyserImport(contenu: string, existants: Iterable<Existant> = []): ResultatImport {
  const deja = new Set<string>();
  for (const e of existants) {
    if (typeof e === 'string') { deja.add(`MOINS5|${e.trim()}`); deja.add(`PLUS5|${e.trim()}`); }
    else deja.add(cleDoublon(e.numeroRceBrut, e.ageBrut));
  }
  const res: ResultatImport = { valides: [], rejets: [], doublons: 0, tranchesPalu: Object.fromEntries(RULES.tranches.palu.map((b) => [b.label, 0])) };
  extraireLignes(contenu).forEach((brute, i) => {
    const ligne = i + 1;
    if (!brute.rce.trim()) return res.rejets.push({ ligne, raison: 'N° RCE manquant' });
    const c = convertirLigne(brute);
    if (!c.ok) return res.rejets.push({ ligne, raison: c.raison });
    const cle = cleDoublon(c.consultation.numeroRceBrut, c.consultation.ageBrut);
    const estCrenas = c.consultation.numeroRceBrut.toUpperCase() === 'CRENAS';
    if (!estCrenas && deja.has(cle)) { res.doublons++; return; }
    const v = validerConsultation(c.consultation);
    if (!v.ok) return res.rejets.push({ ligne, raison: v.erreurs[0] ?? 'Consultation invalide' });
    deja.add(cle);
    res.valides.push(c.consultation);
    // Tranche paludisme : fièvre ou diagnostic paludisme, selon l'âge en jours calculé par parseAge.
    const palu = c.consultation.casFievre === 'Oui' || c.consultation.diagnostics.some((d) => (RULES.diagnosticsPaludisme as readonly string[]).includes(d));
    const age = c.consultation.ageBrut ? parseAge(c.consultation.ageBrut) : null;
    if (palu && age?.ok && age.ageJours !== undefined) res.tranchesPalu[tranchePalu(age.ageJours)]++;
  });
  return res;
}
